import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { TabContext } from '@mui/lab';
import { Accordion, Alert, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useRef } from 'react';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { EMPTY_ANIMATION_DATA } from 'src/constants';
import { AnimationTypeKeys, IAnimation } from 'src/interfaces';
import { lang } from 'src/utils';

import { ContentWrapper } from '../../EditProductPanel.styled';
import { ElementsTabPanel } from './modules/elementsTabPanel/ElementsTabPanel';
import { normalizeAnimationDelayCamps } from './modules/elementsTabPanel/ElementsTabPanel.helper';
import { MoleculeTabPanel } from './modules/moleculeTabPanel/MoleculeTabPanel';
import { IProductAnimationAccordionProps } from './ProductAnimationAccordion.interface';
import {
  AlertContainer,
  AnimationAccordionDetails,
  AnimationAccordionSummary,
  AnimationTypeTab,
  AnimationTypeTabs,
} from './ProductAnimationAccordion.styled';

export const ProductAnimationAccordion = ({
  onChange,
  onTabChange,
  tab,
  animation,
}: IProductAnimationAccordionProps) => {
  const isFirstRender = useRef(true);

  const handleTabChange = (
    _: React.SyntheticEvent,
    animationTypeKey: string,
  ) => {
    onTabChange(animationTypeKey as AnimationTypeKeys);
  };

  const handleAnimationChange = (animation: IAnimation) => {
    onChange(animation);
  };

  useEffect(() => {
    if (tab !== AnimationTypeKeys.OFF && isEmpty(animation)) {
      onChange({ ...EMPTY_ANIMATION_DATA });
    }

    if (!isFirstRender.current && !!animation) {
      isFirstRender.current = false;
      const updatedAnimation = normalizeAnimationDelayCamps(animation);
      onChange(updatedAnimation);
    }
  }, [animation, onChange, tab]);

  return (
    <Accordion defaultExpanded>
      <AnimationAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <InputTextLabel>{lang.productAnimation.title}</InputTextLabel>
      </AnimationAccordionSummary>
      <ContentWrapper>
        <AnimationAccordionDetails>
          <TabContext value={tab || AnimationTypeKeys.OFF}>
            <Typography>{lang.productAnimation.typeSelectorText}</Typography>
            <AnimationTypeTabs
              indicatorColor="none"
              onChange={handleTabChange}
              centered
              value={tab}
              variant="fullWidth">
              <AnimationTypeTab
                label={AnimationTypeKeys.OFF}
                value={AnimationTypeKeys.OFF}
              />
              <AnimationTypeTab
                label={AnimationTypeKeys.MOLECULE}
                value={AnimationTypeKeys.MOLECULE}
                $showBorder
              />
              <AnimationTypeTab
                label={AnimationTypeKeys.ELEMENTS}
                value={AnimationTypeKeys.ELEMENTS}
              />
            </AnimationTypeTabs>
            <AlertContainer>
              <Alert severity="info">
                {lang.productAnimation.alertMessage}
              </Alert>
            </AlertContainer>
            <MoleculeTabPanel
              animation={animation}
              onChange={handleAnimationChange}
            />
            <ElementsTabPanel
              animation={animation}
              onChange={handleAnimationChange}
            />
          </TabContext>
        </AnimationAccordionDetails>
      </ContentWrapper>
    </Accordion>
  );
};
